import { supabase } from '@/integrations/supabase/client';
import type { User } from '@supabase/supabase-js';

export interface GuideStep {
  id: string;
  step_key: string;
  title_key: string;
  description_key: string;
  icon: string;
  route: string;
  sort_order: number;
}

interface Company {
  id: string;
  [key: string]: any;
}

interface StoreSnapshot {
  steps: GuideStep[];
  completedSteps: string[];
  isLoading: boolean;
}

type Listener = () => void;

const EMPTY_SNAPSHOT: StoreSnapshot = {
  steps: [],
  completedSteps: [],
  isLoading: true,
};

let snapshot: StoreSnapshot = EMPTY_SNAPSHOT;
const listeners = new Set<Listener>();

let currentUserId: string | null = null;
let currentCompanyId: string | null = null;
let currentChannel: ReturnType<typeof supabase.channel> | null = null;
let initPromise: Promise<void> | null = null;

function setSnapshot(next: Partial<StoreSnapshot>) {
  snapshot = { ...snapshot, ...next };
  listeners.forEach((l) => l());
}

function reset() {
  if (currentChannel) {
    supabase.removeChannel(currentChannel);
    currentChannel = null;
  }
  currentUserId = null;
  currentCompanyId = null;
  initPromise = null;
  snapshot = EMPTY_SNAPSHOT;
  listeners.forEach((l) => l());
}

async function loadData(userId: string) {
  try {
    const [stepsRes, progressRes] = await Promise.all([
      supabase
        .from('guide_steps')
        .select('*')
        .eq('is_active', true)
        .order('sort_order'),
      supabase
        .from('user_guide_progress')
        .select('step_key')
        .eq('user_id', userId),
    ]);

    if (stepsRes.error) throw stepsRes.error;
    if (progressRes.error) throw progressRes.error;

    setSnapshot({
      steps: (stepsRes.data as GuideStep[]) || [],
      completedSteps: progressRes.data?.map((p: any) => p.step_key) || [],
      isLoading: false,
    });
  } catch (error) {
    console.error('[guideProgressStore] Error loading data:', error);
    setSnapshot({ isLoading: false });
  }
}

function setupRealtime(userId: string) {
  if (currentChannel) {
    supabase.removeChannel(currentChannel);
    currentChannel = null;
  }

  // Unique channel name per user to avoid collisions across instances
  const channelName = `guide-progress-changes-${userId}-${Date.now()}`;

  currentChannel = supabase
    .channel(channelName)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'user_guide_progress',
        filter: `user_id=eq.${userId}`,
      },
      (payload) => {
        const newStepKey = (payload.new as { step_key: string }).step_key;
        if (!snapshot.completedSteps.includes(newStepKey)) {
          setSnapshot({
            completedSteps: [...snapshot.completedSteps, newStepKey],
          });
        }
      }
    )
    .subscribe();
}

export const guideProgressStore = {
  getSnapshot(): StoreSnapshot {
    return snapshot;
  },

  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },

  async init(user: User | null, company: Company | null): Promise<void> {
    if (!user) {
      reset();
      setSnapshot({ isLoading: false });
      return;
    }

    // If user changed, reset and reinit
    if (currentUserId !== user.id) {
      if (currentChannel) {
        supabase.removeChannel(currentChannel);
        currentChannel = null;
      }
      currentUserId = user.id;
      currentCompanyId = company?.id ?? null;
      snapshot = { ...EMPTY_SNAPSHOT };
      initPromise = (async () => {
        setupRealtime(user.id);
        await loadData(user.id);
      })();
      return initPromise;
    }

    // Same user, update company id if needed
    if (company?.id && currentCompanyId !== company.id) {
      currentCompanyId = company.id;
    }

    // Already initializing or initialized
    if (initPromise) return initPromise;
  },

  async markStepComplete(stepKey: string): Promise<void> {
    if (!currentUserId || !currentCompanyId) return;

    // Optimistic update — instant propagation to all subscribers
    if (!snapshot.completedSteps.includes(stepKey)) {
      setSnapshot({
        completedSteps: [...snapshot.completedSteps, stepKey],
      });
    }

    try {
      const { error } = await supabase
        .from('user_guide_progress')
        .insert({
          user_id: currentUserId,
          company_id: currentCompanyId,
          step_key: stepKey,
        });

      // Ignore duplicate errors (idempotent)
      if (error && !error.message.toLowerCase().includes('duplicate')) {
        console.error('[guideProgressStore] markStepComplete error:', error);
      }
    } catch (error) {
      console.error('[guideProgressStore] markStepComplete exception:', error);
    }
  },

  async refetch(): Promise<void> {
    if (!currentUserId) return;
    await loadData(currentUserId);
  },

  // For testing / sign-out
  _reset: reset,
};
